import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Panel, AdminHeader, Stat } from "@/pages/admin/_shared";
import {
  Plugs, Copy, Check, Warning, ShieldCheck, ArrowSquareOut, Pulse,
  Lightning, Receipt
} from "@phosphor-icons/react";

export default function AdminWebhooks() {
  const [cfg, setCfg] = useState(null);
  const [events, setEvents] = useState([]);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    const [c, e] = await Promise.all([
      api.get("/admin/webhooks/config"),
      api.get("/admin/webhooks/recent"),
    ]);
    setCfg(c.data); setEvents(e.data);
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const copy = (text, key) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1800);
  };

  if (!cfg) return <div className="p-10 text-ink-dim">LOADING WEBHOOK CONFIG…</div>;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-webhooks">
      <AdminHeader section="SQUARE WEBHOOK + REDIRECT" title={["WIRE ", "*SQUARE."]} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900 mb-8">
        <Stat label="ENVIRONMENT" value={cfg.environment.toUpperCase()} Icon={Lightning} color={cfg.environment === "production" ? "text-brand-red" : "text-brand-purple"} testid="wh-env" />
        <Stat
          label="SIGNATURE KEY"
          value={cfg.signature_key_configured ? "SET" : "MISSING"}
          Icon={cfg.signature_key_configured ? ShieldCheck : Warning}
          color={cfg.signature_key_configured ? "text-brand-green" : "text-brand-red"}
          testid="wh-sig-status"
        />
        <Stat label="EVENTS / 24H" value={events.length} Icon={Pulse} testid="wh-events-count" />
        <Stat label="VERIFIED" value={events.filter(e => e.verified).length} Icon={Receipt} color="text-brand-green" testid="wh-verified-count" />
      </div>

      {/* Webhook URL card */}
      <Panel title="WEBHOOK URL" tag="PASTE INTO SQUARE" testid="wh-url-panel" className="mb-6">
        <div className="flex items-center gap-2 border border-brand-red bg-brand-red/5 p-4">
          <Plugs size={18} weight="fill" className="text-brand-red shrink-0" />
          <code className="flex-1 text-sm font-mono text-white break-all" data-testid="wh-url">{cfg.webhook_url}</code>
          <button
            onClick={() => copy(cfg.webhook_url, "url")}
            data-testid="wh-copy-url"
            className="bg-brand-red text-black text-[10px] tracking-[0.3em] uppercase font-bold px-3 py-2 hover:bg-white inline-flex items-center gap-1.5"
          >
            {copied === "url" ? <><Check size={12} weight="bold" /> COPIED</> : <><Copy size={12} weight="bold" /> COPY URL</>}
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900 mt-4">
          <KV label="APPLICATION ID" value={cfg.application_id} onCopy={() => copy(cfg.application_id, "app")} k="app" copied={copied} testid="wh-app-id" />
          <KV label="LOCATION ID"    value={cfg.location_id}    onCopy={() => copy(cfg.location_id, "loc")} k="loc" copied={copied} testid="wh-loc-id" />
          <KV label="API VERSION"    value="2025-01-23"          onCopy={() => copy("2025-01-23", "ver")} k="ver" copied={copied} testid="wh-api-ver" />
        </div>
      </Panel>

      {/* Setup steps */}
      <Panel title="SETUP STEPS" tag="9 STEPS" className="mb-6" testid="wh-steps">
        <ol className="space-y-3">
          {cfg.instructions.map((step, i) => (
            <li key={i} className="flex items-start gap-3" data-testid={`wh-step-${i}`}>
              <span className="w-7 h-7 border border-brand-red text-brand-red font-display font-bold text-xs flex items-center justify-center shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-sm text-ink-mute leading-relaxed pt-1">
                {step.replace(/^\d+\.\s*/, "")}
              </span>
            </li>
          ))}
        </ol>
        <a
          href="https://developer.squareup.com/apps"
          target="_blank"
          rel="noreferrer"
          data-testid="wh-square-dash-link"
          className="mt-5 inline-flex items-center gap-2 bg-brand-red text-black text-[10px] tracking-[0.3em] uppercase font-bold px-4 py-2.5 hover:bg-white"
        >
          OPEN SQUARE DEV DASHBOARD <ArrowSquareOut size={12} weight="bold" />
        </a>
      </Panel>

      {/* Events to subscribe */}
      <Panel title="EVENTS TO SUBSCRIBE" tag={cfg.events_to_subscribe.length} className="mb-6" testid="wh-events-subscribe">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
          {cfg.events_to_subscribe.map((ev) => (
            <div key={ev} className="bg-bg-surface p-3 flex items-center gap-2">
              <Check size={12} weight="bold" className="text-brand-green" />
              <code className="text-xs text-white font-mono truncate">{ev}</code>
            </div>
          ))}
        </div>
      </Panel>

      {/* Recent webhook events */}
      <Panel title="RECENT EVENTS RECEIVED" tag="LIVE · POLLING 8s" testid="wh-recent">
        {events.length === 0 ? (
          <div className="text-xs text-ink-dim py-10 text-center" data-testid="wh-recent-empty">
            No webhook events yet. After setting up the URL in Square, click&nbsp;
            <span className="text-white font-bold">Send Test Event</span> in the Square dashboard to verify the connection.
          </div>
        ) : (
          <ul className="space-y-2 max-h-[420px] overflow-y-auto">
            {events.map((e) => (
              <li key={e.id} className={`border p-3 ${e.verified ? "border-brand-green/40 bg-brand-green/5" : "border-brand-orange/40 bg-brand-orange/5"}`} data-testid={`wh-event-${e.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    {e.verified ? <ShieldCheck size={16} weight="fill" className="text-brand-green" /> : <Warning size={16} weight="fill" className="text-brand-orange" />}
                    <code className="text-sm text-white font-mono">{e.type}</code>
                    <span className={`text-[9px] tracking-[0.3em] uppercase px-1.5 py-0.5 border ${
                      e.verified ? "border-brand-green text-brand-green" : "border-brand-orange text-brand-orange"
                    }`}>
                      {e.verified ? "VERIFIED" : "UNVERIFIED"}
                    </span>
                  </div>
                  <span className="text-[10px] tracking-[0.25em] uppercase text-ink-dim">{new Date(e.received_at).toLocaleString()}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function KV({ label, value, onCopy, k, copied, testid }) {
  return (
    <div className="bg-bg-surface p-3 flex items-center justify-between gap-2" data-testid={testid}>
      <div className="min-w-0 flex-1">
        <div className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">{label}</div>
        <code className="block text-xs text-white font-mono truncate mt-0.5">{value}</code>
      </div>
      <button onClick={onCopy} className="text-ink-mute hover:text-brand-red p-1.5" data-testid={`${testid}-copy`}>
        {copied === k ? <Check size={12} weight="bold" className="text-brand-green" /> : <Copy size={12} weight="bold" />}
      </button>
    </div>
  );
}
